import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { IFullDeviceDetails } from "../../../store/api/deviceConfig/types";
import { Chip, Grid } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmbeddedDashboard from "../../../components/Shared/Superset/EmbeddedDashboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { DASHBOARDS, DATEFORMATS, performanceKeys } from "../../../globals/globals";
import { useSelector } from "react-redux";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import EmbeddedCell from "../EmbeddedCell/EmbeddedCell";
import ThresholdCrossingProfile from "../../../components/Configuration/Performance/ThresholdCrossingProfile/ThresholdCrossingProfile";
import ThresholdCrossing from "../../../components/Nodes/PerformanceDisplay/ThresholdCrossing/ThresholdCrossing";
import { capitalizeWords } from "../../../utils/helpers";
import ResourcesChart from "../../../components/ResourcesChart";
import { memoryChartConfig, cpuChartConfig } from "./resourcesChartConfigs";
import { PerformanceProvider, usePerformanceFilters } from "./PerformanceContext";
import { getChartGridSize } from "./chartGroupConfigs";
import { getSiteChartGroups, getSiteFullscreenKeys, getSaSiteChartGroups, getSaSiteFullscreenKeys } from "./Site";
import { getResourcesChartGroups, getResourcesFullscreenKeys } from "./Resources";
import { useStoreDispatch } from "../../../store/hooks";
import { resetKeyword, updateKeyword } from "../../../store/search/reducer";
import PerformanceSubTabs from "./PerformanceSubTabs";
import PanelTabs from "../../../components/Shared/Tab/PanelTabs";

dayjs.extend(duration);

type NetworkModeTabValue = 'nr-sa' | 'nr-nsa';

const PerformanceContent: React.FC = () => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const { state } = location;
  const navigationState = (state || {}) as {
    deviceDetail?: IFullDeviceDetails;
    initialPerformanceTab?: string;
    initialCellSearch?: string;
    navigationRequestId?: number;
  };
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const deviceDetail = (navigationState.deviceDetail || selectedDevice) as IFullDeviceDetails;
  const resolvedNodeType = deviceDetail?.nodeType || selectedDevice?.nodeType || "";
  const initialPerformanceTab = navigationState.initialPerformanceTab;
  const isMe1200 = resolvedNodeType?.toUpperCase() === "ME1200";
  const supportsSaSiteCharts = ["RCP", "ME1200"].includes(resolvedNodeType?.toUpperCase());
  const normalizedInitialPerformanceTab =
    isMe1200 && initialPerformanceTab === "site-e-sa" ? "site" : initialPerformanceTab;
  const initialSiteTab = initialPerformanceTab === "site-e-sa" ? "nr-sa" : "nr-nsa";
  const initialCellSearch = navigationState.initialCellSearch;
  const navigationRequestId = navigationState.navigationRequestId;
  
  // Use shared filter state from context
  const { 
    timeRange, 
    truncate, 
    startDate, 
    endDate, 
    setTimeRange, 
    setTruncate, 
    setStartDate, 
    setEndDate,
    refreshInterval,
    setRefreshInterval,
    incrementRefreshTrigger
  } = usePerformanceFilters();
  
  // Get chart groups based on device node type
  const siteChartGroups = getSiteChartGroups(resolvedNodeType);
  const resourcesChartGroups = getResourcesChartGroups();
  
  // Memoize config objects to prevent unnecessary re-renders
  const memoizedMemoryConfig = useMemo(() => memoryChartConfig, []);
  const memoizedCpuConfig = useMemo(() => cpuChartConfig, []);
  
  // Get fullscreen keys for type-safe state management
  const siteFullscreenKeys = getSiteFullscreenKeys(resolvedNodeType);
  const resourcesFullscreenKeys = getResourcesFullscreenKeys();
  
  // State to manage fullscreen chart (dynamically typed based on available charts)
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);

  // SA site chart groups and fullscreen keys (RCP only)
  const saSiteChartGroups = getSaSiteChartGroups();
  const saSiteFullscreenKeys = getSaSiteFullscreenKeys();
  const [fullscreenSaChart, setFullscreenSaChart] = useState<string | null>(null);
  
  // State to manage chart layout (vertical or horizontal)
  const [chartLayout, setChartLayout] = useState<'vertical' | 'horizontal'>('horizontal');
  const [activeSiteTab, setActiveSiteTab] = useState<NetworkModeTabValue>(initialSiteTab);
  const [activeCellTab, setActiveCellTab] = useState<NetworkModeTabValue>('nr-nsa');

  // Filter bar used in Site E and ResourcesP tabs
  const filterBar = (
    <div className="rounded-xl border bg-card text-card-foreground shadow mb-4">
      <div className="px-6 py-3 flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Time Range</span>
          <select
            value={timeRange}
            onChange={(e) => {
              const value = e.target.value as 'lastWeek' | 'lastMonth' | 'all' | 'custom';
              setTimeRange(value);
              if (value !== 'custom') { setStartDate(null); setEndDate(null); }
            }}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="lastWeek">Last Week</option>
            <option value="lastMonth">Last Month</option>
            <option value="all">All</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {timeRange === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    minWidth: 140,
                    '& .MuiInputBase-root': { fontSize: '0.875rem', backgroundColor: 'var(--background)' },
                    '& .MuiInputBase-input': { color: 'var(--foreground)', fontSize: '0.875rem' },
                    '& .MuiInputLabel-root': { color: 'var(--muted-foreground)', fontSize: '0.875rem' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--foreground)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
                    '& .MuiSvgIcon-root': { color: 'var(--muted-foreground)' },
                  }
                }
              }}
              maxDate={endDate || dayjs()}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    minWidth: 140,
                    '& .MuiInputBase-root': { fontSize: '0.875rem', backgroundColor: 'var(--background)' },
                    '& .MuiInputBase-input': { color: 'var(--foreground)', fontSize: '0.875rem' },
                    '& .MuiInputLabel-root': { color: 'var(--muted-foreground)', fontSize: '0.875rem' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--foreground)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
                    '& .MuiSvgIcon-root': { color: 'var(--muted-foreground)' },
                  }
                }
              }}
              minDate={startDate || undefined}
              maxDate={dayjs()}
            />
          </LocalizationProvider>
        )}

        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Interval</span>
          <select
            value={truncate}
            onChange={(e) => setTruncate(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="minute">Minute</option>
            <option value="hour">Hour</option>
            <option value="day">Day</option>
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Auto-Refresh</span>
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value={0}>Never</option>
            <option value={600000}>10 minutes</option>
            <option value={900000}>15 minutes</option>
            <option value={1800000}>30 minutes</option>
          </select>
        </div>

        <button
          onClick={() => incrementRefreshTrigger()}
          title="Refresh now"
          className="h-8 w-8 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
        >
          <RefreshIcon sx={{ fontSize: 16 }} />
        </button>

        <button
          onClick={() => setChartLayout(chartLayout === 'vertical' ? 'horizontal' : 'vertical')}
          title={chartLayout === 'vertical' ? 'Switch to Horizontal Layout' : 'Switch to Vertical Layout'}
          className="h-8 w-8 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
        >
          {chartLayout === 'vertical' ? <ViewModuleIcon sx={{ fontSize: 16 }} /> : <ViewStreamIcon sx={{ fontSize: 16 }} />}
        </button>
      </div>
    </div>
  );

  // LTE Sites/Cells will use different dashboards than NR Sites/Cells, but the "Resources" dashboard will be the same
  const siteNrSaContent = (
    <div className="bg-background min-h-[100vh] p-2">
      {filterBar}
      <Grid container spacing={6}>
        {fullscreenChart === null && (
          <>
            {/* Show message if no charts available */}
            {siteChartGroups.length === 0 && (
              <Grid item xs={12}>
                <div className="text-center py-16">
                  <h3 className="text-lg font-semibold text-muted-foreground">
                    No Charts Available
                  </h3>
                </div>
              </Grid>
            )}

            {siteChartGroups.map((group) => {
              return (
                <React.Fragment key={group.groupTitle}>
                  <Grid
                    item
                    {...(chartLayout === 'vertical' ? { xs: 12 } : (group.groupGridSize || { xs: 12 }))}
                  >
                    {/* Group Title */}
                    <h2 className="text-lg font-semibold text-foreground mb-4 mt-4 first:mt-0">
                      {group.groupTitle}
                    </h2>

                    <Grid container spacing={6}>
                      {/* Charts in this group */}
                      {group.charts.map((chart, chartIndex) => {
                        const ChartComponent = chart.component;
                        return (
                          <Grid
                            key={chart.fullscreenKey}
                            item
                            {...getChartGridSize(chartLayout, group.charts.length, chartIndex)}
                          >
                            <ChartComponent
                              onFullscreenToggle={() => setFullscreenChart(chart.fullscreenKey)}
                              isFullscreen={false}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </React.Fragment>
              );
            })}
          </>
        )}

        {/* Fullscreen views */}
        {siteFullscreenKeys.map((key) => {
          if (fullscreenChart === key) {
            // Find the chart component for this fullscreen key
            const chartConfig = siteChartGroups
              .flatMap(group => group.charts)
              .find(chart => chart.fullscreenKey === key);

            if (chartConfig) {
              const ChartComponent = chartConfig.component;
              return (
                <Grid item xs={12} key={key}>
                  <ChartComponent
                    onFullscreenToggle={() => setFullscreenChart(null)}
                    isFullscreen={true}
                  />
                </Grid>
              );
            }
          }
          return null;
        })}
      </Grid>
    </div>
  );

  const siteNrNsaContent = (
    <div className="bg-background min-h-[100vh] p-2">
      {filterBar}
      <Grid container spacing={6}>
        {fullscreenSaChart === null && (
          <>
            {!supportsSaSiteCharts ? (
              <Grid item xs={12}>
                <div className="text-center py-16">
                  <h3 className="text-lg font-semibold text-muted-foreground">
                    No Charts Available
                  </h3>
                </div>
              </Grid>
            ) : (
              <>
                {saSiteChartGroups.length === 0 && (
                  <Grid item xs={12}>
                    <div className="text-center py-16">
                      <h3 className="text-lg font-semibold text-muted-foreground">
                        No Charts Available
                      </h3>
                    </div>
                  </Grid>
                )}
                {saSiteChartGroups.map((group) => (
                  <React.Fragment key={group.groupTitle}>
                    <Grid item {...(group.groupGridSize || { xs: 12 })}>
                      <h2 className="text-lg font-semibold text-foreground mb-4 mt-4 first:mt-0">
                        {group.groupTitle}
                      </h2>
                      <Grid container spacing={6}>
                        {group.charts.map((chart, chartIndex) => {
                          const ChartComponent = chart.component;
                          return (
                            <Grid
                              key={chart.fullscreenKey}
                              item
                              {...getChartGridSize(chartLayout, group.charts.length, chartIndex)}
                            >
                              <ChartComponent
                                onFullscreenToggle={() => setFullscreenSaChart(chart.fullscreenKey)}
                                isFullscreen={false}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                  </React.Fragment>
                ))}
              </>
            )}
          </>
        )}
        {supportsSaSiteCharts && saSiteFullscreenKeys.map((key) => {
          if (fullscreenSaChart === key) {
            const chartConfig = saSiteChartGroups
              .flatMap(g => g.charts)
              .find(c => c.fullscreenKey === key);
            if (chartConfig) {
              const ChartComponent = chartConfig.component;
              return (
                <Grid item xs={12} key={key}>
                  <ChartComponent
                    onFullscreenToggle={() => setFullscreenSaChart(null)}
                    isFullscreen={true}
                  />
                </Grid>
              );
            }
          }
          return null;
        })}
      </Grid>
    </div>
  );

  const siteContent = isMe1200 ? (
    <PerformanceSubTabs
      selectedValue={activeSiteTab}
      onChange={(value) => setActiveSiteTab(value as NetworkModeTabValue)}
      tabs={[
        {
          label: "NR-NSA",
          value: "nr-nsa",
          content: siteNrSaContent,
        },
        {
          label: "NR SA",
          value: "nr-sa",
          content: siteNrNsaContent,
        },
      ]}
    />
  ) : (
    siteNrSaContent
  );

  const cellContent = isMe1200 ? (
    <PerformanceSubTabs
      selectedValue={activeCellTab}
      onChange={(value) => setActiveCellTab(value as NetworkModeTabValue)}
      tabs={[
        // NR SA tab hidden for now
        // {
        //   label: "NR SA",
        //   value: "nr-sa",
        //   content: <div className="min-h-[100vh] p-6 text-sm text-muted-foreground">No data available.</div>,
        // },
        {
          label: "NR-NSA",
          value: "nr-nsa",
          content: <EmbeddedCell />,
        },
      ]}
    />
  ) : (
    <EmbeddedCell />
  );

  const devicePerformanceTabData = [
    {
      label: "Site",
      value: "site",
      component: siteContent,
    },
    {
      label: "Cell",
      value: "cell",
      component: cellContent
    },

    {
      label: "Threshold Crossing Profiles",
      value: "threshold-crossing-profiles",
      component: <ThresholdCrossingProfile selectedDevice={selectedDevice} />,
    },
    {
      label: "Threshold Crossing",
      value: "threshold-crossing",
      component: <ThresholdCrossing deviceId={selectedDevice?.objectKey || deviceDetail?.objectKey} />,
    },
    {
      label: "Resources",
      value: "resources",
      component: (
        <div className="bg-background min-h-[100vh] p-2">
          {filterBar}
          <Grid container spacing={6}>
            {fullscreenChart === null && (
              <>
                {resourcesChartGroups.map((group) => (
                  <React.Fragment key={group.groupTitle}>
                    <Grid item xs={12}>
                      <h2 className="text-lg font-semibold text-foreground mb-4 mt-4 first:mt-0">
                        {group.groupTitle}
                      </h2>
                      <Grid container spacing={6}>
                        {group.charts.map((chart, chartIndex) => {
                          const chartConfig = chart.configKey === 'memory' ? memoizedMemoryConfig : memoizedCpuConfig;
                          
                          return (
                            <Grid 
                              key={chart.fullscreenKey} 
                              item 
                              {...getChartGridSize(chartLayout, group.charts.length, chartIndex)}
                            >
                              <ResourcesChart
                                config={chartConfig}
                                onFullscreenToggle={() => setFullscreenChart(chart.fullscreenKey)}
                                isFullscreen={false}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                  </React.Fragment>
                ))}
              </>
            )}
          
          {/* Fullscreen views */}
          {resourcesFullscreenKeys.map((key) => {
            if (fullscreenChart === key) {
              // Map fullscreen key to the config object (using memoized versions)
              const chartConfig = key === 'memory' ? memoizedMemoryConfig : memoizedCpuConfig;
              
              return (
                <Grid item xs={12} key={key}>
                  <ResourcesChart
                    config={chartConfig}
                    onFullscreenToggle={() => setFullscreenChart(null)}
                    isFullscreen={true}
                  />
                </Grid>
              );
            }
            return null;
          })}
          </Grid>
        </div>
      ),
    },
  ];

  const [activeTab, setActiveTab] = useState(() => {
    const fallbackTab = devicePerformanceTabData[0]?.value || "site";
    if (normalizedInitialPerformanceTab && devicePerformanceTabData.some((tab) => tab.value === normalizedInitialPerformanceTab)) {
      return normalizedInitialPerformanceTab;
    }
    return fallbackTab;
  });

  useEffect(() => {
    if (isMe1200) {
      if (initialPerformanceTab === "site-e-sa") {
        setActiveTab("site");
        setActiveSiteTab("nr-nsa");
      } else if (normalizedInitialPerformanceTab && devicePerformanceTabData.some((tab) => tab.value === normalizedInitialPerformanceTab)) {
        setActiveTab(normalizedInitialPerformanceTab);
        if (normalizedInitialPerformanceTab === "site") {
          setActiveSiteTab("nr-sa");
        }
        if (normalizedInitialPerformanceTab === "cell") {
          setActiveCellTab("nr-nsa");
        }
      }
    } else if (initialPerformanceTab && devicePerformanceTabData.some((tab) => tab.value === initialPerformanceTab)) {
      setActiveTab(initialPerformanceTab);
    }

    if (initialCellSearch?.trim()) {
      dispatch(updateKeyword(initialCellSearch.trim()));
    }
  }, [dispatch, initialCellSearch, initialPerformanceTab, navigationRequestId, location.key, normalizedInitialPerformanceTab, isMe1200]);

  const handleTabChange = React.useCallback((nextTab: string) => {
    setActiveTab(nextTab);
    if (nextTab !== "cell") {
      dispatch(resetKeyword());
    }
  }, [dispatch]);

  const handlePmIntervals = (val: any) => {
    if (!val) {
      return "-";
    } else if (val) {
      const interval = dayjs.duration(Math.trunc(Number(val) / 60), "minutes").asMinutes();
      return !!interval ? `${interval}m` : "-";
    } else return "-";
  };

  const itemLabelVal = (item: string) => {
    const { nodeType, ltePmEnable, ngPmEnable, ltePmSampleInterval, ngPmSampleInterval, pmCollected, lastPmPollingTime } = selectedDevice || {};
    if (item === "pmcollection") {
      if (nodeType?.toLowerCase() === "sn") {
        return ltePmEnable ? "Enabled" : "Disabled";
      } else {
        return ngPmEnable ? "Enabled" : "Disabled";
      }
    } else if (item === "kpiStatus") {
      return (
        <Chip
          className="chip-grid"
          variant="outlined"
          sx={{ border: "none" }}
          label={!pmCollected ? "Error" : "Synchronized"}
          icon={!pmCollected ? <SyncProblemIcon color="error" /> : <CheckCircleOutlineIcon className="status-icon" color="success" />}
        />
      );
    } else if (item === "intervals") {
      if (selectedDevice?.nodeType?.toLowerCase() === "sn") {
        return handlePmIntervals(ltePmSampleInterval);
      } else {
        return handlePmIntervals(ngPmSampleInterval);
      }
    } else if (item === "lastPmCollected") {
      return lastPmPollingTime ? dayjs(lastPmPollingTime).format(DATEFORMATS.TS) || "" : "-";
    } else return "";
  };

  return (
    <>
      <div className="flex flex-col h-full min-h-0 bg-background">
        <div className="flex-1 min-h-0 overflow-auto px-4 pb-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {performanceKeys?.map((item, index) => (
              <div key={index} className="rounded-xl border bg-card text-card-foreground shadow">
                <div className="p-6 pt-6">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-muted-foreground">{capitalizeWords(item.label)}</span>
                    <span className="text-2xl font-semibold">{itemLabelVal(item?.key)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border bg-card text-card-foreground shadow">
            <div className="p-4 pt-3">
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <div className="pl-4 pr-1 mt-1">
                  <div style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                      {devicePerformanceTabData.map((tab) => (
                        <TabsTrigger
                          key={tab.value}
                          value={tab.value}
                          className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                          {tab.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                </div>
                {devicePerformanceTabData.map((tab) => (
                  <TabsContent key={tab.value} value={tab.value} className="pt-4">
                    {tab.component}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Performance: React.FC = () => {
  return (
    <PerformanceProvider>
      <PerformanceContent />
    </PerformanceProvider>
  );
};

export default Performance;
